// Het vlak Overzicht: zes kaarten en het maandverslag (W6 ronde 2, besluit V1).
//
// Sinds deze ronde het standaardvlak van Beheer: wie binnenkomt ziet eerst of
// er iets op hem wacht en of de machine draait. Elke kaart is een deur — klik
// en je staat in het vlak waar het getal vandaan komt (`kaart.vlak`).
//
// Het scherm rekent hier NIETS uit. Het getal (al opgemaakt, Nederlandse
// duizendtallen), de toelichting, het stoplicht en de reden komen kant-en-klaar
// van GET /api/beheer/overzicht. Een kaart zonder waarde draagt volgens het
// contract altijd een reden; die staat op de plek van het getal, zodat een
// niet-gemeten vak nooit als nul leest (W8-ontwerpregel 1). Hetzelfde geldt
// voor het maandverslag: de zinnen bouwt de server uit tellingen (research
// §3.1), wij zetten ze alleen onder elkaar.

import { useEffect, useState } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { Kaart, Maandverslag, OverzichtResponse } from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { Stoplicht } from "./Stoplicht";

/** Onder deze breedte één kaart per rij: drie naast elkaar wordt op een
 *  telefoon een rij postzegels waarin het getal niet meer past. */
const SMAL = "(max-width: 720px)";

/** Volgt de schermbreedte. Inline stijlen kennen geen media query, en een
 *  losse klasse in styles.css zou het overgezette V1-blad aanpassen; dus
 *  vraagt het vlak het de browser zelf. Zonder matchMedia (tests, oude
 *  browser) blijft het de brede weergave. */
export function useSmal(): boolean {
  const vraag = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(SMAL).matches
      : false;
  const [smal, setSmal] = useState(vraag);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(SMAL);
    const bij = () => setSmal(mq.matches);
    mq.addEventListener?.("change", bij);
    return () => mq.removeEventListener?.("change", bij);
  }, []);
  return smal;
}

/** "10 september, 19:04" — of niets als de server iets onleesbaars stuurt. */
function gemetenTekst(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("nl-NL", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Overzicht({ onKiesVlak }: { onKiesVlak: (vlak: string) => void }) {
  const overzicht = useResource<OverzichtResponse>(() => api.overzicht(), []);
  const smal = useSmal();

  if (overzicht.error) {
    return (
      <Card style={{ padding: 18 }}>
        <LoadError reason={overzicht.error} onRetry={overzicht.reload} />
      </Card>
    );
  }
  if (!overzicht.data) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="het overzicht wordt opgehaald" />
      </Card>
    );
  }

  const { kaarten, maandverslag, gemetenOp } = overzicht.data;
  const gemeten = gemetenTekst(gemetenOp);

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: smal ? "1fr" : "repeat(3, minmax(0, 1fr))",
          gap: 12,
          marginBottom: 28,
        }}
      >
        {kaarten.map((k) => (
          <KaartTegel key={k.sleutel} kaart={k} onKies={() => onKiesVlak(k.vlak)} />
        ))}
      </div>

      <MaandverslagBlok verslag={maandverslag} />

      {gemeten && (
        <p style={{ fontSize: 12, color: color.faint, marginTop: 10 }}>Gemeten op {gemeten}.</p>
      )}
    </div>
  );
}

function KaartTegel({ kaart, onKies }: { kaart: Kaart; onKies: () => void }) {
  return (
    // Een knop, geen div met onClick: dan werkt hij ook met Tab en Enter.
    <button
      type="button"
      onClick={onKies}
      title="Open dit vlak"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        textAlign: "left",
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: "16px 18px",
        minHeight: 128,
        font: "inherit",
        cursor: "pointer",
        transition: "border-color 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = color.disabled)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = color.line)}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          width: "100%",
        }}
      >
        <span style={{ ...sectionLabel, marginBottom: 0 }}>{kaart.titel}</span>
        <Stoplicht stand={kaart.stand} />
      </span>

      {kaart.waarde !== null ? (
        // Zelfde maatvoering als de Kpi op Funding en ReeksWidget op
        // Arbeidsmarkt: 25/700, tabellarische cijfers.
        <span
          style={{
            fontSize: 25,
            fontWeight: weight.bold,
            color: color.ink,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {kaart.waarde}
        </span>
      ) : (
        <span
          style={{ fontSize: size.meta, color: color.soft, fontStyle: "italic", lineHeight: 1.5 }}
        >
          {/* Het contract belooft een reden; komt die toch niet, dan zeggen we
              dát in plaats van het vak leeg te laten. */}
          {kaart.reden ?? "De server gaf geen getal en geen reden."}
        </span>
      )}

      {kaart.toelichting && (
        <span style={{ fontSize: 12, color: color.faint, lineHeight: 1.5 }}>
          {kaart.toelichting}
        </span>
      )}
    </button>
  );
}

function MaandverslagBlok({ verslag }: { verslag: Maandverslag }) {
  const kop = verslag.maandNaam
    ? `Wat deed de machine in ${verslag.maandNaam}`
    : "Wat deed de machine";

  return (
    <Card style={{ padding: "16px 18px" }}>
      <h2
        style={{
          fontSize: size.body,
          fontWeight: weight.bold,
          color: color.ink,
          marginBottom: 8,
        }}
      >
        {kop}
      </h2>

      {/* De reden staat bóven de zinnen: "dit is augustus, niet september"
          moet je lezen voordat je de getallen leest. */}
      {verslag.reden && (
        <p
          style={{
            fontSize: size.meta,
            color: color.accentAmber,
            lineHeight: 1.5,
            marginBottom: 8,
          }}
        >
          {verslag.reden}
        </p>
      )}

      {verslag.zinnen.length > 0 ? (
        <ul style={{ paddingLeft: 18, display: "flex", flexDirection: "column", gap: 4 }}>
          {verslag.zinnen.map((zin, i) => (
            <li key={i} style={{ fontSize: size.body, color: color.body, lineHeight: 1.55 }}>
              {zin}
            </li>
          ))}
        </ul>
      ) : (
        !verslag.reden && <EmptyReason reason="de server gaf voor deze maand geen zinnen" />
      )}

      {verslag.ontbreekt.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 3 }}>
          {verslag.ontbreekt.map((o) => (
            <p key={o.wat} style={{ fontSize: 12, color: color.faint, lineHeight: 1.5 }}>
              {o.wat}: {o.reden}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}
